import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TableLazyLoadEvent } from 'primeng/table';
import { firstValueFrom } from 'rxjs';
import { HasPermissionDirective } from '../../../../../../directives/has-permission.directive';
import { automaticMeasureDefinition } from '../../../../../../entity-definitions/automatic-measure.definition';
import { ManualMeasuresService } from '../../../../../../services/api/generated/manual-measures/manual-measures.service';
import { AssetMeasureTypeOptionsService } from '../../../../../../services/asset-measure-type-options.service';
import { UsedLocationOptionsService } from '../../../../../../services/used-location-options.service';
import { buildLocationFilterColumn } from '../../../../../../utils/location-filter';
import { ToastService } from '../../../../../../services/toast.service';
import { AssetStore } from '../../../../../../stores/asset.store';
import { AutomaticMeasureStore } from '../../../../../../stores/automatic-measure.store';
import { ColumnConfig } from '../../../../../../types/entity-definition.interface';
import { DetailedAutomaticMeasure } from '../../../../../../types/measure.interface';
import { MeasureType } from '../../../../../../types/table.interface';
import { notifyMeasureDeletionError } from '../../../../../../utils/measure-deletion-error.util';
import { PaginatedListHelper } from '../../../../../../utils/paginated-list.helper';
import { LazyLoadParams } from '../../../../../../types/lazy-load.interface';
import { RouteUtils } from '../../../../../../utils/route.utils';
import { GenericParentGridComponent } from '../../../../../generic/generic-parent-grid.component';

@Component({
  selector: 'app-automatic-measure-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GenericParentGridComponent, HasPermissionDirective, TranslocoModule],
  templateUrl: './automatic-measure-list.component.html',
})
export class AutomaticMeasureListComponent implements OnInit {
  private readonly assetStore = inject(AssetStore);
  private readonly measureTypeOptions = inject(AssetMeasureTypeOptionsService);
  private readonly usedLocations = inject(UsedLocationOptionsService);
  private readonly store = inject(AutomaticMeasureStore);
  private readonly measureApi = inject(ManualMeasuresService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translations = toSignal(this.translateService.selectTranslation());

  readonly helper = new PaginatedListHelper<DetailedAutomaticMeasure>(this.translateService, {
    globalFilterFields: ['name', 'measureTypeName', 'localisationName'],
    errorKey: 'common.errors.automaticMeasureLoadFailed',
    stateKey: 'automatic-measures-grid',
    loadAssetById: async id => {
      await this.assetStore.loadById(id);
    },
    isAssetLoaded: id => !!this.assetStore.byId()(id),
  });

  readonly entityDefinitionWithPermissions = computed(() => {
    this.translations();
    const columns: ColumnConfig<DetailedAutomaticMeasure>[] = [
      {
        field: 'name',
        headerKey: 'common.labels.name',
        sortable: true,
        filterable: true,
        template: 'measureName',
        class: 'min-w-[200px]',
        contentClass: 'line-clamp-1',
        showTooltip: true,
      },
      {
        field: 'measureTypeName',
        filterField: 'measureTypeId',
        headerKey: 'common.labels.type',
        sortable: true,
        filterable: 'multiselect',
        options: this.measureTypeOptions.options(),
      },
      buildLocationFilterColumn<DetailedAutomaticMeasure>(this.usedLocations.options('automatic')(), 'server'),
    ];
    const assetId = this.helper.state.currentAssetId() ?? undefined;
    return {
      ...automaticMeasureDefinition,
      defaultSortField: null,
      list: { columns },
      actionButtons: {
        ...automaticMeasureDefinition.actionButtons,
        parent: {
          ...automaticMeasureDefinition.actionButtons?.parent,
          addPermission: { method: 'postAutomaticMeasureList' as const, assetId },
          editPermission: { method: 'putAutomaticMeasureDetail' as const, assetId },
          deletePermission: { method: 'deleteAutomaticMeasureDetail' as const, assetId },
        },
      },
      rowLinkHref: (measure: DetailedAutomaticMeasure) => RouteUtils.getMeasurePath(measure.assetId, 'automatic', measure.id),
    };
  });

  readonly tablePermissionConfig = computed(() => ({
    method: 'getAutomaticMeasureDetail' as const,
    assetId: this.helper.state.currentAssetId() ?? undefined,
  }));

  readonly gridStateKey = computed(() => this.helper.buildStateKey('automatic-measures-grid'));

  readonly noAssetSelectedKey = (): string => 'common.messages.noAssetSelected.automaticMeasures';

  constructor() {
    effect(() => {
      const assetId = this.helper.state.currentAssetId();
      this.measureTypeOptions.load(assetId);
      this.usedLocations.load(assetId, 'automatic');
    });
  }

  ngOnInit(): void {
    this.helper.setupLangChangeSubscription(this.destroyRef, () =>
      this.loadData(this.helper.lastParams ?? this.helper.createInitialParams()),
    );
    this.helper.setupRouteSubscription(this.route, this.destroyRef, async shouldLoad => {
      if (shouldLoad) {
        await this.loadData(this.helper.createInitialParams());
      }
    });
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    await this.helper.handleLazyLoad(event, params => this.loadData(params));
  }

  private async loadData(params: LazyLoadParams): Promise<void> {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId) return;

    await this.helper.executeLoad(params, async () => {
      const response = await firstValueFrom(
        this.measureApi.getDetailedMeasuresResource({
          assetId,
          type: MeasureType.AUTOMATIC,
          page: params.pagination.page,
          pageSize: params.pagination.pageSize,
          ...(params.sorts?.length ? { sorts: JSON.stringify(params.sorts) } : {}),
          ...(params.filters?.length ? { filters: JSON.stringify(params.filters) } : {}),
        }),
      );
      return {
        data: response.measures as unknown as DetailedAutomaticMeasure[],
        total: response.total,
        page: response.page,
        pageSize: response.limit,
      };
    });
  }

  createMeasure(): void {
    const assetId = this.helper.state.currentAssetId();
    if (!assetId) return;
    void this.router.navigate(RouteUtils.getMeasurePath(assetId, 'automatic', 'new'));
  }

  deleteMeasure(measure: DetailedAutomaticMeasure): void {
    if (!measure?.id) {
      console.error(this.translateService.translate('common.errors.invalidAutomaticMeasureDeletion'));
      return;
    }

    this.store
      .delete(measure.id)
      .then(() => this.helper.reloadWithLastParams(params => this.loadData(params)))
      .catch(error => {
        notifyMeasureDeletionError(error, this.toastService);
        console.error(this.translateService.translate('common.errors.automaticMeasureDeleteFailed'), error);
      });
  }
}
